import React, { useEffect, useState } from 'react';
import { APIError, useAxios } from '../core/_axios';
import { Service } from '../core/_models';
import LoadingSpinner from './LoadingSpinner';
import { DateTime } from 'luxon';

interface Memory {
  unique_identifier: string;
  memory_text: string;
  photos: Array<{ url: string; uploaded_at: string }>;
  signer_info: {
    name: string;
    email?: string;
    relationship?: string;
  };
  created_at: string;
  metadata: any;
}

interface MemoriesListProps {
  service: Service;
}

export const MemoriesList: React.FC<MemoriesListProps> = ({ service }) => {
  const axios = useAxios();
  const [loading, setLoading] = useState(true);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [viewingPhotoIndex, setViewingPhotoIndex] = useState<number | null>(null);
  const [allPhotos, setAllPhotos] = useState<Array<{ url: string; memory: Memory }>>([]);

  useEffect(() => {
    fetchMemories();
  }, [service.unique_identifier]);

  const fetchMemories = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/v1/app/authenticated/services/${service.unique_identifier}/memories`);
      
      if (response.data && response.data.data) {
        const fetchedMemories = response.data.data.memories;
        setMemories(fetchedMemories);
        
        // Collect all photos from all memories
        const photos: Array<{ url: string; memory: Memory }> = [];
        fetchedMemories.forEach((memory: Memory) => {
          if (memory.photos && memory.photos.length > 0) {
            memory.photos.forEach(photo => {
              photos.push({ url: photo.url, memory });
            });
          }
        });
        setAllPhotos(photos);
      } else {
        setError('Failed to load memories');
      }
    } catch (err) {
      console.error('Error fetching memories:', err);
      const errorMessage = err instanceof APIError ? err.message : 'Unable to load memories';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  if (memories.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <i className="ki-filled ki-heart text-4xl text-gray-400 mb-3"></i>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Memories Shared Yet</h3>
        <p className="text-gray-600">
          Memories will appear here when family and friends share them through email links.
        </p>
      </div>
    );
  }

  const deceasedName = service.public_data.deceased_person?.full_name || 'your loved one';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Memories of {deceasedName} ({memories.length})
        </h2>
      </div>
      
      {memories.map((memory) => (
        <div key={memory.unique_identifier} className="bg-white rounded-lg shadow-sm border p-5">
          {/* Header with signer info */}
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-gray-900">{memory.signer_info.name}</h4>
              <div className="text-sm text-gray-600">
                {memory.signer_info.relationship && (
                  <span>{memory.signer_info.relationship}</span>
                )}
                {memory.signer_info.relationship && memory.signer_info.email && (
                  <span className="mx-1">•</span>
                )}
                {memory.signer_info.email && (
                  <span>{memory.signer_info.email}</span>
                )}
              </div>
            </div>
            <span className="text-xs text-gray-500">
              {DateTime.fromISO(memory.created_at).toRelative()}
            </span>
          </div>
          
          {/* Memory text */}
          <div className="mb-4">
            <p className="text-gray-800 whitespace-pre-wrap">{memory.memory_text}</p>
          </div>
          
          {/* Photos */}
          {memory.photos && memory.photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-4">
              {memory.photos.map((photo, index) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    src={photo.url} 
                    alt={`Memory photo ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => {
                      // Find the index of this photo in the allPhotos array
                      const photoIndex = allPhotos.findIndex(p => p.url === photo.url);
                      setViewingPhotoIndex(photoIndex);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Source info */}
          {memory.metadata?.tracking_id && (
            <div className="mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">
                Shared via email campaign
              </span>
            </div>
          )}
        </div>
      ))}
      
      {/* Photo Carousel Modal */}
      {viewingPhotoIndex !== null && allPhotos.length > 0 && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          style={{ margin: 0, padding: 0 }}
          onClick={() => setViewingPhotoIndex(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close button */}
            <button
              onClick={() => setViewingPhotoIndex(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Previous button */}
            {viewingPhotoIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingPhotoIndex(viewingPhotoIndex - 1);
                }}
                className="absolute left-4 text-white hover:text-gray-300 z-10"
              >
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {/* Image */}
            <div className="max-w-full max-h-full flex flex-col items-center">
              <img
                src={allPhotos[viewingPhotoIndex].url}
                alt="Memory photo"
                className="max-w-full max-h-[80vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Photo info */}
              <div className="mt-4 text-white text-center">
                <p className="text-sm">
                  Photo {viewingPhotoIndex + 1} of {allPhotos.length}
                </p>
                <p className="text-xs mt-1 text-gray-300">
                  From {allPhotos[viewingPhotoIndex].memory.signer_info.name}
                </p>
              </div>
            </div>
            
            {/* Next button */}
            {viewingPhotoIndex < allPhotos.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingPhotoIndex(viewingPhotoIndex + 1);
                }}
                className="absolute right-4 text-white hover:text-gray-300 z-10"
              >
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};